// scenes/.../scan_renderpipeline.ts

import RenderPipeline from "../../../lib/services/render/renderpipeline";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";

export default class ScanRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;
  texture_name: string;

  sampler: GPUSampler | null = null;
  texture: GPUTexture | null = null;
  texture_view: GPUTextureView | null = null;
  rect_buffer: GPUBuffer | null = null;
  bind_group: GPUBindGroup | null = null;

  constructor(key: string, shader_name: string, texture_name: string) {
    super(key + "_scan");
    this.label = key + "_scan";
    this.shader_name = shader_name;
    this.texture_name = texture_name;
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

    

    // Rect uniform: left, top, right, bottom — 16 bytes.
    this.rect_buffer = device.createBuffer({
      label: this.label + "_rect",
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.sampler = device.createSampler({
      label: this.label + "_sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    });

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: { module: shader, entryPoint: "vs_main", buffers: [] },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [{
          format,
          blend: {
            color: {
              operation: "add",
              srcFactor: "src-alpha",
              dstFactor: "one-minus-src-alpha",
            },
            alpha: {
              operation: "add",
              srcFactor: "one",
              dstFactor: "one-minus-src-alpha",
            },
          },
        }],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: false,
        depthCompare: "always",
      },
    });

    const tex_resource = await resource_service.getTexture(this.texture_name + ".png");
    const texture_asset = tex_resource.value;
    
    this.bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      label: this.label + "_bind",
      entries: [
        { binding: 0, resource: { buffer: this.rect_buffer } },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: texture_asset.view },
      ],
    });
  }

  /**
   * Set the corner's NDC rectangle: (left, top, right, bottom).
   * In NDC, +y is up, so top > bottom.
   */
  update_params(
    device: GPUDevice,
    left: number,
    top: number,
    right: number,
    bottom: number
  ): void {
    if (!this.rect_buffer) return;
    device.queue.writeBuffer(
      this.rect_buffer,
      0,
      new Float32Array([left, top, right, bottom])
    );
  }

  prepare(_device: GPUDevice): void {}

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group) return;
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.draw(6);
  }
}